/**
 * Chiamate alle API degli allarmi su Telegram (servizio `notifications`).
 *
 * Alertmanager consegna gli allarmi del cluster a un webhook di
 * `notifications`, che li inoltra a una chat di Telegram. Da qui un
 * amministratore sceglie il bot e la chat, accende o spegne l'inoltro, manda
 * una prova e scollega.
 *
 * Il **token del bot va in una direzione sola**: entra con `saveTelegram` e con
 * `findTelegramChats`, e nessuna risposta lo riporta. `TelegramAlertStatus`
 * non ha un campo in cui potrebbe finire.
 *
 * Provvisorio dichiarato: percorsi e tipi scritti a mano (CLAUDE.md §2.3),
 * presidiati da `alerts.test.ts`.
 */

import type { ApiClient } from '../client';

/** Percorso della configurazione degli allarmi su Telegram. */
const TELEGRAM_PATH = '/api/v1/notifications/alerts/telegram';

/** Provenienza del token del bot in uso. */
export type TelegramTokenSource = 'web' | 'environment';

/** Stato della consegna degli allarmi su Telegram. Non contiene il token. */
export interface TelegramAlertStatus {
  /** True se token e chat sono entrambi noti al server. */
  configured: boolean;
  /** True se l'inoltro degli allarmi è acceso. */
  enabled: boolean;
  /** Nome utente del bot, senza `@`; `null` se non noto. */
  bot_username: string | null;
  /** Identificativo della chat che riceve gli allarmi; `null` se non scelta. */
  chat_id: string | null;
  /** Nome della chat, se Telegram lo ha riferito. */
  chat_title: string | null;
  /** Da dove viene il token: salvato dalla dashboard o dalla configurazione del server. */
  token_source: TelegramTokenSource | null;
  /** Istante dell'ultima consegna riuscita, ISO 8601. */
  last_sent_at: string | null;
  /** Ultimo errore di consegna, come lo ha scritto Telegram o il servizio. */
  last_error: string | null;
  /** Istante dell'ultima modifica della configurazione, ISO 8601. */
  updated_at: string | null;
  /** True se il server ha la chiave con cui cifra il token a riposo. */
  key_configured: boolean;
}

/** Configurazione da salvare. */
export interface TelegramAlertSettings {
  /** Token nuovo del bot; `null` o assente per tenere quello salvato. */
  bot_token?: string | null;
  /** Chat che riceve gli allarmi. */
  chat_id: string;
  /** True per accendere l'inoltro. */
  enabled: boolean;
}

/** Una chat che ha scritto di recente al bot. */
export interface TelegramChat {
  /** Identificativo della chat (negativo per i gruppi). */
  id: string;
  /** Nome della chat o del gruppo. */
  title: string;
  /** Tipo secondo Telegram: `private`, `group`, `supergroup`, `channel`. */
  type: string;
}

/** Risposta della ricerca delle chat. */
export interface TelegramChatList {
  /** Chat trovate; vuoto se nessuno ha scritto al bot di recente. */
  chats: TelegramChat[];
}

/** Operazioni sugli allarmi, esposte alla UI. */
export class AlertsApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Legge lo stato della consegna su Telegram.
   *
   * @returns Lo stato, senza il token.
   * @throws ApiError 403 per chi non amministra la piattaforma.
   */
  getTelegram(): Promise<TelegramAlertStatus> {
    return this.client.request<TelegramAlertStatus>('GET', TELEGRAM_PATH);
  }

  /**
   * Salva token, chat e interruttore. Il server verifica il token e manda un
   * messaggio di prova alla chat prima di salvare.
   *
   * @param settings Configurazione da salvare.
   * @returns Lo stato aggiornato.
   * @throws ApiError 422 `telegram_rejected` o `telegram_token_missing`, 409 `alerts_key_missing`.
   */
  saveTelegram(settings: TelegramAlertSettings): Promise<TelegramAlertStatus> {
    return this.client.request<TelegramAlertStatus>('PUT', TELEGRAM_PATH, settings);
  }

  /**
   * Cerca le chat che hanno scritto di recente al bot.
   *
   * @param botToken Token da provare; `null` per usare quello salvato.
   * @returns Le chat trovate.
   * @throws ApiError 422 `telegram_rejected` o `telegram_token_missing`.
   */
  findTelegramChats(botToken: string | null): Promise<TelegramChatList> {
    return this.client.request<TelegramChatList>('POST', `${TELEGRAM_PATH}/chats`, {
      bot_token: botToken,
    });
  }

  /**
   * Manda un messaggio di prova alla chat salvata.
   *
   * @returns Nulla (204).
   * @throws ApiError 409 `alerts_not_configured`, 422 `telegram_rejected`.
   */
  testTelegram(): Promise<void> {
    return this.client.request<void>('POST', `${TELEGRAM_PATH}/test`);
  }

  /**
   * Scollega il bot: cancella token e chat salvati.
   *
   * @returns Nulla (204).
   */
  disconnectTelegram(): Promise<void> {
    return this.client.request<void>('DELETE', TELEGRAM_PATH);
  }
}
